import React, { useState, useEffect } from 'react';
import { useStore } from '../../store';
import { usePermissions } from '../../hooks/usePermissions';
import { X, AlertCircle } from 'lucide-react';
import { STATUS, PRIORITY, TASK_TYPE } from '../../utils';
import AssigneePicker from '../UI/AssigneePicker';

export default function CreateTaskModal({ onClose, defaultStatus = 'backlog', defaultParent = null }) {
  const { createTask, activeProject, sprints } = useStore();
  const { canCreateTask } = usePermissions();
  const [form, setForm] = useState({
    title: '', description: '', status: defaultStatus,
    priority: 'medium', type: 'task', dueDate: '', tags: '',
    sprint: '', parent: defaultParent || '', assignees: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Проверяем права при открытии модального окна
    if (!canCreateTask()) {
      // Закрываем модал если нет прав
      setTimeout(() => onClose(), 100);
    }
  }, [canCreateTask, onClose]);

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await createTask({
        ...form,
        project: activeProject._id,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        sprint: form.sprint || null,
        parent: form.parent || null,
        dueDate: form.dueDate || null,
      });
      onClose();
    } catch (err) {
      alert(err.message);
    } finally { setLoading(false); }
  };

  const activeSprints = sprints.filter(s => s.status !== 'completed');
  const members = activeProject?.members || [];

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold">Новая задача</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">Название *</label>
            <input className="input" placeholder="Что нужно сделать?" autoFocus
              value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
          </div>

          <div>
            <label className="label">Описание</label>
            <textarea className="input resize-none" rows={3} placeholder="Детали задачи..."
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Тип</label>
              <select className="input" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                {Object.entries(TASK_TYPE).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Приоритет</label>
              <select className="input" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Статус</label>
              <select className="input" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Срок</label>
              <input className="input" type="date" value={form.dueDate}
                onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
            </div>
            {activeSprints.length > 0 && (
              <div>
                <label className="label">Спринт</label>
                <select className="input" value={form.sprint} onChange={e => setForm(p => ({ ...p, sprint: e.target.value }))}>
                  <option value="">Бэклог</option>
                  {activeSprints.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="label">Исполнители</label>
            <AssigneePicker
              value={form.assignees}
              onChange={ids => setForm(p => ({ ...p, assignees: ids }))}
              members={members}
            />
          </div>

          <div>
            <label className="label">Теги (через запятую)</label>
            <input className="input" placeholder="frontend, api, bug"
              value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Отмена</button>
            <button type="submit" className="btn-primary flex-1 justify-center" disabled={loading}>
              {loading ? 'Создание...' : 'Создать задачу'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
